// One invoice in the Preschool invoices list: a dense row on wide screens, a compact card on phones. Click to see
// its lines.

import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RestoreIcon from "@mui/icons-material/Restore";
import UndoIcon from "@mui/icons-material/Undo";
import { alpha, useTheme } from "@mui/material/styles";
import { formatPounds, termHours, type PreschoolInvoice } from "../../shared/preschoolInvoices";
import { STATUS_LABEL, statusOf } from "./list";

export interface RowActions {
  newForChild: (invoice: PreschoolInvoice) => void;
  edit: (invoice: PreschoolInvoice) => void;
  setPaid: (invoice: PreschoolInvoice, paid: boolean) => void;
  setCancelled: (invoice: PreschoolInvoice, cancelled: boolean) => void;
  receipt: (invoice: PreschoolInvoice) => void;
  preview: (invoice: PreschoolInvoice) => void;
}

/** Grid columns shared by the header and the rows (wide screens). */
export const ROW_COLUMNS = "88px 76px minmax(170px, 1fr) minmax(110px, 150px) 92px 140px 88px 92px 104px";

export const shortDate = (iso: string) =>
  iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" }) : "—";

const money = (pence: number) => `£${formatPounds(pence)}`;
const dayMonth = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const KIND_LABEL = { term: "", registration: "Registration", other: "Extra" };

const STATUS_COLOR = { notIssued: "warning", unpaid: "info", paid: "success", nothingDue: "default", cancelled: "default" } as const;

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]!.toUpperCase()).join("") || "?";

export const RowHeader: React.FC = () => (
  <Box sx={{
    display: { xs: "none", md: "grid" }, gridTemplateColumns: ROW_COLUMNS, columnGap: 1.5, px: 2, py: 1,
    color: "text.secondary", typography: "caption", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4,
  }}>
    <span>Invoice</span><span>Issued</span><span>Child</span><span>Period</span>
    <Box textAlign="right">Parent pays</Box><span>Parent status</span>
    <Box textAlign="right">Funding</Box><Box textAlign="right">Total</Box><span />
  </Box>
);

export const InvoiceRow: React.FC<{ invoice: PreschoolInvoice; busy: boolean; actions: RowActions; hideChild?: boolean }> = ({
  invoice, busy, actions, hideChild,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const status = statusOf(invoice);
  const cancelled = status === "cancelled";
  const colour = STATUS_COLOR[status];
  const accent = colour === "default" ? theme.palette.text.disabled : theme.palette[colour].main;

  const closeMenu = () => setMenuAnchor(null);
  const item = (label: string, icon: React.ReactNode, onClick: () => void) => (
    <MenuItem key={label} onClick={() => { closeMenu(); onClick(); }}>
      <ListItemIcon>{icon}</ListItemIcon><ListItemText>{label}</ListItemText>
    </MenuItem>
  );
  const menuItems: React.ReactNode[] = [];
  if (!cancelled) {
    menuItems.push(item(status === "notIssued" ? "Edit and issue" : "Edit and reissue", <AutorenewIcon fontSize="small" />, () => actions.edit(invoice)));
    menuItems.push(item("View PDF", <VisibilityIcon fontSize="small" />, () => actions.preview(invoice)));
    if (invoice.totalPence > 0 && status !== "notIssued") {
      menuItems.push(item(invoice.receipt ? "Create receipt again" : "Create receipt", <ReceiptLongIcon fontSize="small" />, () => actions.receipt(invoice)));
    }
    if (invoice.paid) menuItems.push(item("Mark as unpaid", <UndoIcon fontSize="small" />, () => actions.setPaid(invoice, false)));
    menuItems.push(item("Cancel invoice", <BlockIcon fontSize="small" />, () => actions.setCancelled(invoice, true)));
  } else {
    menuItems.push(item("Restore", <RestoreIcon fontSize="small" />, () => actions.setCancelled(invoice, false)));
  }
  menuItems.push(item(`New invoice for ${invoice.childName.split(" ")[0]}`, <AddIcon fontSize="small" />, () => actions.newForChild(invoice)));

  const statusCell = (
    <>
      {status === "notIssued" && (
        <Tooltip title="Numbered in the sheet but never generated or sent">
          <Button size="small" variant="contained" color="warning" disabled={busy} startIcon={<SendIcon />}
            onClick={(e) => { e.stopPropagation(); actions.edit(invoice); }}>
            Issue
          </Button>
        </Tooltip>
      )}
      {status === "unpaid" && (
        <Button size="small" variant="outlined" color="success" disabled={busy} startIcon={<CheckCircleIcon />}
          onClick={(e) => { e.stopPropagation(); actions.setPaid(invoice, true); }}>
          Mark paid
        </Button>
      )}
      {status === "paid" && (
        <Tooltip title={invoice.receipt ? `Receipt drafted (payment ${shortDate(invoice.receipt.paymentDate)})` : "Paid · click for a receipt"}>
          <Button size="small" variant="outlined" color="success" disabled={busy} sx={{ whiteSpace: "nowrap" }}
            startIcon={invoice.receipt ? <CheckCircleIcon /> : <ReceiptLongIcon />}
            onClick={(e) => { e.stopPropagation(); actions.receipt(invoice); }}>
            {invoice.paidDate ? `Paid ${dayMonth(invoice.paidDate)}` : "Paid"}
          </Button>
        </Tooltip>
      )}
      {(status === "nothingDue" || status === "cancelled") && <Chip size="small" label={STATUS_LABEL[status]} />}
    </>
  );

  const fundedHours = invoice.calc ? termHours(invoice.calc.funded) : 0;

  return (
    <Box sx={{
      borderBottom: 1, borderColor: "divider", borderLeft: `4px solid ${accent}`, opacity: cancelled ? 0.55 : 1,
      "&:hover": { bgcolor: "action.hover" }, transition: "background-color 120ms",
    }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          cursor: "pointer", px: 2, py: 1,
          display: "grid", alignItems: "center", columnGap: 1.5, rowGap: 0.5,
          gridTemplateColumns: { xs: "1fr auto", md: ROW_COLUMNS },
          gridTemplateAreas: {
            xs: `"ref amount" "child status" "meta meta"`,
            md: "none",
          },
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ gridArea: { xs: "ref", md: "auto" } }}>
          <Typography fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums", textDecoration: cancelled ? "line-through" : "none" }}>
            {invoice.ref}
          </Typography>
          {invoice.draftError && (
            <Tooltip title={`Gmail draft failed: ${invoice.draftError}`}><ErrorOutlineIcon color="error" fontSize="small" /></Tooltip>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>{shortDate(invoice.issuedDate || invoice.dueDate)}</Typography>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ gridArea: { xs: "child", md: "auto" }, minWidth: 0 }}>
          {!hideChild && (
            <Avatar sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 600, bgcolor: alpha(accent, 0.15), color: accent }}>
              {initials(invoice.childName)}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            {!hideChild && <Typography noWrap fontWeight={600}>{invoice.childName}</Typography>}
            <Typography variant="body2" color="text.secondary" noWrap>{invoice.parentName || "No parent details"}</Typography>
          </Box>
        </Stack>
        <Box sx={{ gridArea: { xs: "meta", md: "auto" }, minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {invoice.period}
            {KIND_LABEL[invoice.kind] && <Chip size="small" variant="outlined" label={KIND_LABEL[invoice.kind]} sx={{ ml: 0.75, height: 20 }} />}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { md: "none" } }}>
            Issued {shortDate(invoice.issuedDate || invoice.dueDate)}
            {invoice.fundingPence ? ` · funding ${money(invoice.fundingPence)} · total ${money(invoice.totalPence + invoice.fundingPence)}` : ""}
          </Typography>
        </Box>
        <Typography sx={{ gridArea: { xs: "amount", md: "auto" }, textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {money(invoice.totalPence)}
        </Typography>
        <Box sx={{ gridArea: { xs: "status", md: "auto" }, justifySelf: { xs: "end", md: "start" } }}>{statusCell}</Box>
        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", md: "block" }, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          {invoice.fundingPence ? money(invoice.fundingPence) : "—"}
        </Typography>
        <Typography variant="body2" sx={{ display: { xs: "none", md: "block" }, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          {money(invoice.totalPence + (invoice.fundingPence || 0))}
        </Typography>
        <Stack direction="row" justifyContent="flex-end" sx={{ display: { xs: "none", md: "flex" } }}>
          <Tooltip title="View PDF">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); actions.preview(invoice); }}><VisibilityIcon fontSize="small" /></IconButton>
          </Tooltip>
          {!cancelled && (
            <Tooltip title={status === "notIssued" ? "Edit and issue" : "Edit and reissue"}>
              <IconButton size="small" disabled={busy} onClick={(e) => { e.stopPropagation(); actions.edit(invoice); }}><EditIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
          <IconButton size="small" disabled={busy} aria-label="More actions" onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2, pb: 2, pl: { md: "124px" } }}>
          <Table size="small" sx={{ maxWidth: 620, "& td": { borderBottom: "none", py: 0.25, px: 1 } }}>
            <TableBody>
              {invoice.items.filter((i) => i.totalPence !== 0 || i.description === "Universal Funding").map((i) => (
                <TableRow key={i.description}>
                  <TableCell>{i.description}</TableCell>
                  <TableCell align="right">{i.quantity}</TableCell>
                  <TableCell align="right">{money(i.unitPricePence)}</TableCell>
                  <TableCell align="right">{money(i.totalPence)}</TableCell>
                </TableRow>
              ))}
              {invoice.discountPence > 0 && (
                <TableRow><TableCell>Discount</TableCell><TableCell /><TableCell /><TableCell align="right">−{money(invoice.discountPence)}</TableCell></TableRow>
              )}
              {invoice.fundingPence > 0 && (
                <TableRow sx={{ "& td": { color: "text.secondary" } }}>
                  <TableCell>Council funding (not on the invoice)</TableCell>
                  <TableCell align="right">{fundedHours} hours</TableCell>
                  <TableCell align="right">{money(invoice.calc?.funded.fundingRatePence ?? 0)}</TableCell>
                  <TableCell align="right">{money(invoice.fundingPence)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {invoice.email || "no email"} · due {shortDate(invoice.dueDate)}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => actions.preview(invoice)}>View PDF</Button>
            {!cancelled && (
              <Button size="small" variant="outlined" disabled={busy} startIcon={<EditIcon />} onClick={() => actions.edit(invoice)}>
                {status === "notIssued" ? "Edit and issue" : "Edit and reissue"}
              </Button>
            )}
            {!cancelled && status !== "notIssued" && invoice.totalPence > 0 && (
              <Button size="small" variant="outlined" disabled={busy} startIcon={<ReceiptLongIcon />} onClick={() => actions.receipt(invoice)}>
                {invoice.receipt ? "Receipt again" : "Create receipt"}
              </Button>
            )}
            <Button size="small" variant="outlined" disabled={busy} startIcon={<MoreVertIcon />} onClick={(e) => setMenuAnchor(e.currentTarget)}>More</Button>
          </Stack>
        </Box>
      </Collapse>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>{menuItems}</Menu>
    </Box>
  );
};
